import Skeleton from "./Skeleton";
import SkeletonTextLine from "./SkeletonTextLine";

/**
 * Cold-boot silhouette of the desktop front door (DesktopAuthPage, Figma
 * "D0.boot.welcome"): the form column on the left, the hero panel on the
 * right, in the page's own boxes so nothing moves when the page replaces it.
 * Both doors (`/signup`, `/login`) share it: they differ by their words only.
 * Pure render: no queries, no auth reads.
 */
export default function DesktopAuthBootSkeleton(): React.JSX.Element {
  return (
    <div className="flex h-dvh w-full justify-center bg-ds-canvas">
      <div className="flex w-full max-w-desktop-shell">
        <main className="flex w-desktop-auth-column shrink-0 flex-col items-center justify-center-safe overflow-y-auto p-8">
          <div className="flex w-full max-w-lg flex-col gap-7 p-11">
            {/* Title (text-desktop-display 42/46) and subtitle (2 lines,
                text-ui-body 15/20). */}
            <div className="flex flex-col gap-2">
              <SkeletonTextLine width="72%" fontSize={42} lineHeight={46} />
              <div className="flex flex-col">
                <SkeletonTextLine width="100%" fontSize={15} lineHeight={20} />
                <SkeletonTextLine width="58%" fontSize={15} lineHeight={20} />
              </div>
            </div>

            {/* Apple and Google (Button size md, 48 high). */}
            <div className="flex flex-col gap-3">
              <Skeleton $variant="rounded" width="100%" height={48} />
              <Skeleton $variant="rounded" width="100%" height={48} />
            </div>

            {/* The OR divider keeps its lines. */}
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-ds-slate-200" />
              <Skeleton width={16} height={11} />
              <div className="h-px flex-1 bg-ds-slate-200" />
            </div>

            {/* The email field keeps its outline: the reader sees where to
                type. Then Continue. */}
            <div className="flex flex-col gap-5">
              <div className="flex h-12 items-center rounded-xl border border-ds-slate-200 bg-ds-white px-4">
                <Skeleton width={160} height={14} />
              </div>
              <Skeleton $variant="rounded" width="100%" height={48} />
            </div>

            {/* The other door, then the legal caption (2 lines). */}
            <div className="flex justify-center py-2.5">
              <Skeleton width={180} height={15} />
            </div>
            <div className="flex flex-col items-center">
              <SkeletonTextLine width={400} fontSize={11} lineHeight={16} />
              <SkeletonTextLine width={300} fontSize={11} lineHeight={16} />
            </div>
          </div>
        </main>

        {/* The hero panel: logo on top; title, ask bar and trusted-by row at
            the bottom. */}
        <aside className="flex min-w-0 flex-1 flex-col p-8">
          <div className="flex flex-1 flex-col justify-between rounded-xl bg-ds-slate-100 p-8">
            <Skeleton $variant="rounded" width={140} height={52} />
            <div className="flex flex-col gap-4">
              {/* text-desktop-hero 56/54: its line box is shorter than the
                  font, so the bars take the cap height to stay two lines. */}
              <div className="flex flex-col">
                <SkeletonTextLine width="70%" fontSize={42} lineHeight={54} />
                <SkeletonTextLine width="48%" fontSize={42} lineHeight={54} />
              </div>
              <Skeleton className="rounded-2xl" width="100%" height={56} />
              <div className="flex flex-col gap-3">
                <div className="flex -space-x-3">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <Skeleton
                      key={index}
                      $variant="circular"
                      className="border-2 border-ds-slate-100"
                      width={40}
                      height={40}
                    />
                  ))}
                </div>
                <SkeletonTextLine width={300} fontSize={14} lineHeight={20} />
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
